import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTrigger,
  useToast,
  useTheme,
  cn,
} from '@mgdlead/ui';
import type { ThemePreference } from '@mgdlead/ui';
import { KeyRound, Monitor, Moon, Plug, Sun, Users } from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { AlertsSection } from '../../../features/alerts/AlertsSection';
import { GoalsSection } from '../../../features/goals/GoalsSection';
import { PlanoDoWorkspace } from '../../../features/billing/PlanoDoWorkspace';
import { NativeSelect } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { useSession } from '../../../auth/useSession';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import { useUpdateWorkspace } from '../../../lib/api';
import { updateProfile } from '../../../lib/auth';
import { LOCALE_OPTIONS, TIMEZONE_OPTIONS } from '../../../lib/locale-options';
import { profileSchema, workspaceSchema } from '../../../lib/schemas';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { useZodForm } from '../../../lib/useZodForm';

const TABS = [
  'perfil',
  'workspace',
  'avisos',
  'metas',
  'plano',
  'aparencia',
  'seguranca',
  'integracoes',
] as const;
type SettingsTab = (typeof TABS)[number];

function isTab(value: string | null): value is SettingsTab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function SettingsPage() {
  const { workspace, role } = useWorkspace();
  useDocumentTitle('Configurações');

  // a aba vive na URL: recarregar ou compartilhar o link cai na mesma aba
  const [params, setParams] = useSearchParams();
  const raw = params.get('aba');
  const pedida: SettingsTab = isTab(raw) ? raw : 'perfil';

  /**
   * Quem não tem `billing:read` não tem a aba — e também não pode cair nela
   * digitando `?aba=plano` na URL. Esconder o gatilho e deixar o painel
   * acessível pelo endereço é o jeito de a tela existir sem caminho: o menu
   * não tranca nada, e quem testa por URL direta nunca vê o buraco.
   */
  const podeVerPlano = can(role, 'billing:read');
  const active: SettingsTab = pedida === 'plano' && !podeVerPlano ? 'perfil' : pedida;

  return (
    <>
      <PageHeader
        title="Configurações"
        description={`Sua conta e o workspace ${workspace.name}.`}
        action={
          can(role, 'member:read') ? (
            <Link
              to={workspacePath(workspace.slug, 'configuracoes/membros')}
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-border bg-surface px-3 text-sm font-medium text-foreground hover:border-border-strong hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background max-md:min-h-11 md:w-auto"
            >
              <Users aria-hidden="true" className="size-4" />
              Membros
            </Link>
          ) : null
        }
      />

      <Tabs value={active} onValueChange={(next) => setParams({ aba: next }, { replace: true })}>
        <TabsList label="Seções das configurações">
          <TabsTrigger value="perfil">Perfil</TabsTrigger>
          <TabsTrigger value="workspace">Workspace</TabsTrigger>
          <TabsTrigger value="avisos">Avisos</TabsTrigger>
          <TabsTrigger value="metas">Metas</TabsTrigger>
          {podeVerPlano ? <TabsTrigger value="plano">Plano</TabsTrigger> : null}
          <TabsTrigger value="aparencia">Aparência</TabsTrigger>
          <TabsTrigger value="seguranca">Segurança</TabsTrigger>
          <TabsTrigger value="integracoes">Integrações</TabsTrigger>
        </TabsList>

        <TabsPanel value="perfil">
          <ProfileSection />
        </TabsPanel>

        <TabsPanel value="workspace">
          <WorkspaceSection />
        </TabsPanel>

        <TabsPanel value="avisos">
          <AlertsSection />
        </TabsPanel>

        <TabsPanel value="metas">
          <GoalsSection />
        </TabsPanel>

        {podeVerPlano ? (
          <TabsPanel value="plano">
            {/*
              O MESMO componente da tela `/w/:slug/assinatura`. Duas entradas,
              uma implementação: quem está mexendo em workspace e equipe acha o
              plano aqui, e quem vem pelo menu acha na tela própria.
            */}
            <PlanoDoWorkspace />
          </TabsPanel>
        ) : null}

        <TabsPanel value="aparencia">
          <AppearanceSection />
        </TabsPanel>

        <TabsPanel value="seguranca">
          <Card padded={false}>
            <EmptyState
              kind="no-data"
              icon={KeyRound}
              title="Chega na Fase 12"
              description="Troca de senha, sessões ativas com encerramento remoto e registro de auditoria dos acessos."
            />
          </Card>
        </TabsPanel>

        <TabsPanel value="integracoes">
          <Card padded={false}>
            <EmptyState
              kind="no-data"
              icon={Plug}
              title="Chega nas Fases 2 e 8"
              description="Conexão com o provedor de WhatsApp (Fase 2) e com a conta de anúncios da Meta (Fase 8). Token de provedor nunca aparece na tela nem em log."
            />
          </Card>
        </TabsPanel>
      </Tabs>
    </>
  );
}

/* ========================================================================== */
/*  Perfil                                                                     */
/* ========================================================================== */

function ProfileSection() {
  const { user } = useSession();
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();

  const onSubmit = useCallback(
    async (values: { name: string }) => {
      await updateProfile({ name: values.name });
      toast({ title: 'Perfil atualizado', tone: 'success' });
    },
    [toast],
  );

  const form = useZodForm({
    schema: profileSchema,
    initialValues: { name: user?.name ?? '' },
    onSubmit,
  });

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>
        <CardTitle>Seu perfil</CardTitle>
        <CardDescription>Vale para todos os workspaces em que você participa.</CardDescription>
      </CardHeader>

      <form noValidate onSubmit={form.handleSubmit} className="flex max-w-md flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="Nome" error={form.errors.name} required>
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="name"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field label="E-mail" hint="A troca de e-mail exige confirmação e chega na Fase 12.">
          {(control) => <Input {...control} value={user?.email ?? ''} readOnly disabled />}
        </Field>

        <Field label="Seu papel neste workspace" hint="Definido por quem administra o workspace.">
          {(control) => (
            <Input {...control} value={`${role} · ${workspace.name}`} readOnly disabled />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          block
          loading={form.pending}
          loadingLabel="Salvando"
          className="md:w-auto md:self-start"
        >
          Salvar perfil
        </Button>
      </form>

      <div className="border-t border-border pt-3">
        <Link
          to={workspacePath(workspace.slug, 'configuracoes/membros')}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Users aria-hidden="true" className="size-4" />
          Gerenciar membros do workspace
        </Link>
      </div>
    </Card>
  );
}

/* ========================================================================== */
/*  Workspace                                                                  */
/* ========================================================================== */

function WorkspaceSection() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  const updateWorkspace = useUpdateWorkspace();
  const editable = can(role, 'workspace:update');

  const onSubmit = useCallback(
    async (values: { name: string; timezone: string; locale: string }) => {
      await updateWorkspace.mutateAsync({ workspaceId: workspace.id, ...values });
      toast({ title: 'Workspace atualizado', tone: 'success' });
    },
    [toast, updateWorkspace, workspace.id],
  );

  const form = useZodForm({
    schema: workspaceSchema,
    initialValues: {
      name: workspace.name,
      timezone: workspace.timezone,
      locale: workspace.locale,
    },
    onSubmit,
  });

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>
        <CardTitle>Workspace</CardTitle>
        <CardDescription>
          O fuso define o que conta como &quot;hoje&quot; em toda métrica do painel.
        </CardDescription>
      </CardHeader>

      {!editable ? (
        <FormAlert tone="info">
          Seu papel ({role}) permite ver, mas não alterar as informações do workspace.
        </FormAlert>
      ) : null}

      <form noValidate onSubmit={form.handleSubmit} className="flex max-w-md flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="Nome" error={form.errors.name} required disabled={!editable}>
          {(control) => (
            <Input
              {...control}
              name="name"
              value={form.values.name}
              disabled={!editable}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field label="Identificador na URL" hint={`/w/${workspace.slug}`}>
          {(control) => <Input {...control} value={workspace.slug} readOnly disabled />}
        </Field>

        <Field label="Fuso horário" error={form.errors.timezone} required disabled={!editable}>
          {(control) => (
            <NativeSelect
              {...control}
              name="timezone"
              value={form.values.timezone}
              disabled={!editable}
              invalid={form.errors.timezone !== undefined}
              onChange={(event) => form.setField('timezone', event.target.value)}
            >
              {TIMEZONE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        <Field label="Idioma" error={form.errors.locale} required disabled={!editable}>
          {(control) => (
            <NativeSelect
              {...control}
              name="locale"
              value={form.values.locale}
              disabled={!editable}
              invalid={form.errors.locale !== undefined}
              onChange={(event) => form.setField('locale', event.target.value)}
            >
              {LOCALE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>

        {editable ? (
          <Button
            type="submit"
            variant="primary"
            block
            loading={form.pending}
            loadingLabel="Salvando"
            className="md:w-auto md:self-start"
          >
            Salvar workspace
          </Button>
        ) : null}
      </form>
    </Card>
  );
}

/* ========================================================================== */
/*  Aparência                                                                  */
/* ========================================================================== */

const THEME_CHOICES: readonly {
  id: ThemePreference;
  label: string;
  hint: string;
  icon: typeof Sun;
}[] = [
  { id: 'light', label: 'Claro', hint: 'Sempre claro', icon: Sun },
  { id: 'dark', label: 'Escuro', hint: 'Sempre escuro', icon: Moon },
  { id: 'system', label: 'Sistema', hint: 'Acompanha o aparelho', icon: Monitor },
];

function AppearanceSection() {
  const { theme, resolvedTheme, setTheme } = useTheme();

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader>
        <CardTitle>Aparência</CardTitle>
        <CardDescription>
          A escolha vale neste navegador. Em &quot;Sistema&quot;, quem decide é o aparelho — hoje
          ele está em {resolvedTheme === 'dark' ? 'escuro' : 'claro'}.
        </CardDescription>
      </CardHeader>

      <fieldset className="flex flex-col gap-2">
        <legend className="sr-only">Tema do painel</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {THEME_CHOICES.map((choice) => {
            const Icon = choice.icon;
            const checked = theme === choice.id;
            return (
              <label
                key={choice.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-3 max-md:min-h-11',
                  'transition-colors duration-[var(--duration-micro)]',
                  checked
                    ? 'border-primary bg-muted'
                    : 'border-border hover:border-border-strong hover:bg-muted/40',
                  'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
                )}
              >
                <input
                  type="radio"
                  name="theme"
                  value={choice.id}
                  checked={checked}
                  onChange={() => setTheme(choice.id)}
                  className="accent-primary size-4 shrink-0"
                />
                <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{choice.label}</span>
                  <span className="block text-xs text-muted-foreground">{choice.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </Card>
  );
}
